'use client';

import React, { useState, useRef, useCallback, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useToast } from '@/components/ui/Toast';
import { BRAND_COLORS, PDF_CONFIG, UI_STYLES } from '@/constants/pdfConfig';
import UploadZone from '@/components/pdf/UploadZone';
import FileListItem from '@/components/pdf/FileListItem';
import { usePDFWorker } from '@/hooks/usePDFWorker';
import { setCustomTag } from '@/components/analytics/ClarityTracker';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
}

const MERGE_THEME = {
  primary: '#E8445A',
  primaryLight: '#FFF0F2',
  primaryBorder: '#FFD6DB',
  primaryShadow: 'rgba(232, 68, 90, 0.28)',
};

export default function MergePDFUploader() {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDone, setIsDone] = useState(false);
  const [mergedBlob, setMergedBlob] = useState<Blob | null>(null);

  const { showToast } = useToast();
  const { processPDFs, isProcessing, progress } = usePDFWorker();
  const addMoreInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (newFiles: FileList | null) => {
      if (!newFiles) return;

      const validFiles = Array.from(newFiles).filter(
        (f) =>
          (f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')) &&
          f.size <= PDF_CONFIG.MAX_FILE_SIZE
      );

      if (validFiles.length < newFiles.length) {
        showToast(
          `${newFiles.length - validFiles.length} file(s) skipped (invalid type or size).`,
          'error'
        );
      }

      if (validFiles.length > 0) {
        const mapped: UploadedFile[] = validFiles.map((f) => ({
          id: crypto.randomUUID(),
          name: f.name,
          size: f.size,
          type: f.type,
          file: f,
        }));

        const updatedFiles = [...files, ...mapped];
        setFiles(updatedFiles);
        setIsDone(false);
      }
    },
    [files, showToast]
  );

  const removeFile = useCallback(
    (id: string) => {
      if (isProcessing) return;
      setFiles((prev) => {
        const next = prev.filter((f) => f.id !== id);
        setCustomTag('merge_file_count', String(next.length));
        return next;
      });
      setIsDone(false);
    },
    [isProcessing]
  );

  const moveFile = useCallback(
    (index: number, direction: 'up' | 'down') => {
      if (isProcessing) return;
      setFiles((prev) => {
        const arr = [...prev];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= arr.length) return arr;
        [arr[index], arr[swapIndex]] = [arr[swapIndex], arr[index]];
        setCustomTag('merge_file_reordered', direction);
        return arr;
      });
    },
    [isProcessing]
  );

  const formatSize = useCallback((bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  const totalSize = useMemo(() => files.reduce((acc, f) => acc + f.size, 0), [files]);

  const handleMerge = async () => {
    if (files.length < 2) return;

    try {
      const { blob, errors } = await processPDFs(
        'MERGE_PDFS',
        files.map((f) => ({ blob: f.file, name: f.name }))
      );

      if (errors.length > 0) {
        errors.forEach((err) => {
          const message = err.isEncrypted
            ? `"${err.fileName}" is password protected and was skipped.`
            : `"${err.fileName}" is corrupted/invalid and was skipped.`;
          const link = err.isEncrypted
            ? { text: 'Unlock this PDF', href: '/unlock-pdf' }
            : undefined;
          showToast(message, 'error', link);

          // Clarity: per-file error type
          setCustomTag(
            err.isEncrypted ? 'merge_error_encrypted' : 'merge_error_corrupted',
            err.fileName
          );
        });

        // Remove invalid files
        const errorFileNames = new Set(errors.map((e) => e.fileName));
        setFiles((prev) => prev.filter((f) => !errorFileNames.has(f.name)));
      }

      const successfulFilesCount = files.length - errors.length;

      if (blob && successfulFilesCount >= 2) {
        setMergedBlob(blob);
        setIsDone(true);
        showToast(
          errors.length === 0
            ? 'All PDFs merged successfully!'
            : `Merged ${successfulFilesCount} files.`,
          errors.length === 0 ? 'success' : 'info'
        );
      } else {
        showToast('At least 2 valid PDF files are required for merging.', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to merge PDFs', 'error');
      // Analytics: unexpected merge failure
      setCustomTag('merge_failed', err.message || 'unknown');
    }
  };

  const handleCancelSelection = useCallback(() => {
    setFiles([]);
    setIsDone(false);
    setMergedBlob(null);
    setCustomTag('merge_selection_cancelled', 'true');
    showToast('File selection cleared.', 'info');
  }, [showToast]);

  const handleReset = useCallback(() => {
    setFiles([]);
    setIsDone(false);
    setMergedBlob(null);
  }, []);

  const handleDownload = useCallback(() => {
    if (!mergedBlob) return;
    const url = URL.createObjectURL(mergedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'merged.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
    // Analytics: download triggered
    setCustomTag('tool', 'merge-pdf');
    setCustomTag('merge_download', 'true');
    setCustomTag('merge_output_size_kb', String(Math.round(mergedBlob.size / 1024)));
  }, [mergedBlob]);

  const handleAddMoreClick = useCallback(() => {
    addMoreInputRef.current?.click();
  }, []);

  // Memoized file list for performance
  const fileListItems = useMemo(() => {
    return files.map((file, i) => (
      <FileListItem
        key={file.id}
        id={file.id}
        name={file.name}
        size={formatSize(file.size)}
        index={i}
        totalFiles={files.length}
        isMerging={isProcessing}
        onRemove={removeFile}
        onMove={moveFile}
      />
    ));
  }, [files, formatSize, isProcessing, removeFile, moveFile]);

  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      {/* Upload Zone */}
      {files.length === 0 && (
        <UploadZone
          onFilesSelected={handleFiles}
          multiple={true}
          title="Drop PDF files here"
          subtitle="or click to browse — supports multiple files"
          buttonText="Select PDF Files"
          dragTitle="Drop your PDFs here!"
        />
      )}

      {/* File List */}
      {files.length > 0 && !isDone && (
        <div className="flex flex-col lg:flex-row gap-6 mt-4">
          {/* ================= LEFT ================= */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6 bg-brand-surface p-4 rounded-3xl border border-brand-border">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-brand-dark font-heading mb-0.5 truncate">
                  {files.length} File{files.length > 1 ? 's' : ''} selected
                </h2>
                <div className="flex items-center gap-3 text-xs text-brand-muted font-body">
                  <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white border border-brand-border">
                    <Icon name="DocumentIcon" size={12} aria-hidden="true" />
                    {files.length} File{files.length > 1 ? 's' : ''}
                  </span>
                  <span>{formatSize(totalSize)}</span>
                </div>
              </div>
              <button
                onClick={handleCancelSelection}
                disabled={isProcessing}
                className="w-10 h-10 flex items-center justify-center rounded-xl transition-all border group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
                style={{
                  background: MERGE_THEME.primaryLight,
                  borderColor: MERGE_THEME.primaryBorder,
                  color: MERGE_THEME.primary,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = MERGE_THEME.primary;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = MERGE_THEME.primaryLight;
                  e.currentTarget.style.color = MERGE_THEME.primary;
                }}
                aria-label="Remove file and start over"
                title="Remove file"
              >
                <Icon name="XMarkIcon" size={20} aria-hidden="true" />
              </button>
            </div>

            <div className="flex items-center justify-between mb-4">
              <input
                ref={addMoreInputRef}
                id="add-more-input"
                type="file"
                accept=".pdf,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>
            <div className="max-h-[70vh] overflow-y-auto pr-2 pb-20 lg:pb-0">
              <div className="space-y-2 mb-5">{fileListItems}</div>
            </div>
          </div>

          {/* ================= RIGHT SIDEBAR ================= */}
          <div className="w-full lg:w-[280px]">
            {/* Progress Overlay */}
            {isProcessing && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-body text-[13px] text-brand-mid">Merging files...</span>
                  <span className="font-heading font-bold text-[13px] text-primary">
                    {Math.round(progress)}%
                  </span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden bg-primary-mid">
                  <div
                    className="progress-bar h-full bg-primary"
                    style={{
                      width: `${progress}%`,
                      transition: 'width 0.2s ease-out',
                    }}
                  />
                </div>
              </div>
            )}

            <div className="p-3 mb-4 rounded-xl border border-brand-border bg-brand-surface">
              <div className="grid grid-cols-3 gap-y-2 text-[11px] text-brand-muted">
                <span className="flex items-center gap-1">
                  <Icon name="ArrowUpIcon" size={13} />
                  <Icon name="ArrowDownIcon" size={13} />
                  <span>Reorder</span>
                </span>

                <span className="flex items-center gap-1">
                  <Icon name="XMarkIcon" size={13} />
                  Remove
                </span>
              </div>
            </div>

            <button
              onClick={handleAddMoreClick}
              disabled={isProcessing}
              className="w-full py-3 rounded-xl border border-brand-border text-brand-mid font-semibold"
            >
              Add More PDFs
            </button>

            {/* Action Button */}
            {!isProcessing && (
              <button
                onClick={handleMerge}
                disabled={files.length < 2}
                className="w-full py-4 rounded-2xl font-heading font-bold text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed text-white border-none mt-2"
                style={{
                  background: files.length >= 2 ? '#E8445A' : '#ccc',
                  cursor: files.length >= 2 ? 'pointer' : 'not-allowed',
                  boxShadow: files.length >= 2 ? '0 6px 20px rgba(232,68,90,0.28)' : 'none',
                }}
              >
                <span className="flex items-center justify-center gap-2">
                  Merge {files.length} PDF{files.length !== 1 ? 's' : ''}
                </span>
              </button>
            )}

            {files.length < 2 && !isProcessing && (
              <p className="text-center mt-2 font-body text-[12px] text-brand-muted">
                Add at least 2 PDF files to merge
              </p>
            )}
          </div>
        </div>
      )}

      {/* Done State */}
      {isDone && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 pulse-anim bg-green-50">
            <Icon name="CheckBadgeIcon" size={42} variant="solid" className="text-green-600" />
          </div>

          <h3 className="font-heading font-bold mb-2 text-[20px] text-brand-dark">
            Merge Complete!
          </h3>

          <p className="text-brand-mid text-[14px] font-body mb-6">
            Your {files.length} PDFs have been merged into one file.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              className="sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary text-white border-none font-heading font-bold text-[15px] cursor-pointer shadow-[0_6px_20px_rgba(232,68,90,0.28)]"
              onClick={handleDownload}
            >
              <Icon name="ArrowDownTrayIcon" size={18} variant="solid" />
              Download Merged PDF
            </button>

            <button
              onClick={() => {
                setIsDone(false);
              }}
              className="px-8 py-4 rounded-full border-2 border-brand-border font-heading font-bold text-brand-mid hover:bg-brand-surface transition-all active:scale-95 flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
            >
              <Icon name="PencilSquareIcon" size={18} />
              Edit Selection
            </button>

            <button
              onClick={handleReset}
              className="px-8 py-4 rounded-full border-2 border-brand-border font-heading font-bold text-brand-mid hover:bg-brand-surface transition-all active:scale-95 flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
            >
              <Icon name="ArrowPathIcon" size={18} />
              Start Fresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
